/**
 * The person side panel: a drawer from the right over Import Detail.
 *
 * A React-rendered `role="dialog"` + `aria-modal`, NOT a native `<dialog>`
 * with `showModal()` like `CompanyDetailDrawer`. The top layer is the reason:
 * the panel's own actions open the page's compare modal (a fixed `z-50`
 * overlay) and fire sonner toasts, and anything outside a modal `<dialog>` is
 * both painted under it and made inert. The price is doing by hand what
 * `showModal()` does for free, and all of it is here:
 *
 *   - focus moves INTO the drawer on open (the close button);
 *   - Tab / Shift+Tab wrap inside it;
 *   - Escape closes (the host returns focus to the avatar that opened it —
 *     it owns the table, the drawer does not);
 *   - a scrim covers the page, and a click on it closes.
 *
 * ←/→ and j/k walk the current page's rows (‹ ›). They are ignored while the
 * focus is in a text field or on the tab list, where arrows already mean
 * something, and with any modifier held.
 *
 * THE KEYS ARE HEARD AT THE DOCUMENT, and the drawer is focusable
 * (`tabIndex={-1}`). Both are load-bearing: with the handler on the drawer
 * alone, focus could leave it without the user doing anything unusual — a click
 * on plain text in it, or ‹ › disabling the arrow that had focus at a list end —
 * and from `<body>` Escape, the arrows and the Tab trap all went dead
 * (/pr-review #954 iter 1). The focusable container makes a click inside land
 * focus on the drawer; the document listener covers whatever is left.
 *
 * `suspended` is the second layer's switch: while the host shows a modal the
 * panel opened (the compare modal), the drawer is `inert` and deaf, so ←/→,
 * Escape and Tab act on the modal's page and not on the person behind it; when
 * it lifts, focus goes back to the control that was focused when it began.
 */
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { type ReactNode, useEffect, useId, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { CopyLinkPopover, type ShareLink } from "./CopyLinkPopover";

export interface PanelPosition {
  /** 0-based index of the open person among the navigable rows. */
  index: number;
  total: number;
}

export interface PersonPanelProps {
  listTitle: string;
  /** Null when the open handle is not among the current page's rows. */
  position: PanelPosition | null;
  /** The page's rows have not loaded yet (a deep link): no position to state,
   *  and no "not on this page" claim either — it would not be true yet. */
  positionLoading?: boolean;
  /** The page's rows could not be loaded: no position, and no claim that the
   *  person is off the page either — the page does not know. */
  positionError?: boolean;
  /** A modal the panel opened is on screen: the drawer goes inert and ignores
   *  keys until it lifts. */
  suspended?: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  shareLinks: ShareLink[];
  /** The standalone page for the open item. Omitted = no "Open as page"
   *  (a company list's panel before the company page exists). */
  pagePath?: string;
  /** Wording for what the drawer holds. Defaults to a person; the company
   *  lists reuse this drawer for companies (epic 2026-09-25-chopbook). */
  noun?: PanelNoun;
  /** Prefix for every `data-testid` in the drawer. Default `person-panel`. */
  testIdPrefix?: string;
  /** `wide` (default) covers most of the page; `half` leaves the list
   *  readable beside it, as the company-list prototype draws it. */
  size?: "wide" | "half";
  children: (nameId: string) => ReactNode;
}

export interface PanelNoun {
  /** Capitalised, for the dialog label: "Person" → "Person details". */
  singular: string;
  /** Lower case, for the arrows and the off-page hint: "person". */
  lower: string;
}

const PERSON: PanelNoun = { singular: "Person", lower: "person" };

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

const iconButton =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-text-secondary hover:bg-surface-raised hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";

export function PersonPanel({
  listTitle,
  position,
  positionLoading = false,
  positionError = false,
  suspended = false,
  onPrev,
  onNext,
  onClose,
  shareLinks,
  pagePath,
  noun = PERSON,
  testIdPrefix = "person-panel",
  size = "wide",
  children,
}: PersonPanelProps) {
  const tid = (suffix = "") => `${testIdPrefix}${suffix}`;
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const nameId = useId();
  const hintId = useId();

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const canPrev = position !== null && position.index > 0;
  const canNext = position !== null && position.index < position.total - 1;

  // An arrow that had focus and is now disabled (Next pressed onto the last
  // row) no longer holds it: hand it to the other arrow, else to the drawer.
  useEffect(() => {
    const active = document.activeElement;
    const lost =
      (active === nextRef.current && !canNext) || (active === prevRef.current && !canPrev);
    if (!lost) return;
    if (active === nextRef.current && canPrev) prevRef.current?.focus();
    else if (active === prevRef.current && canNext) nextRef.current?.focus();
    else drawerRef.current?.focus();
  }, [canPrev, canNext]);

  // Suspend / resume. The focused control is captured in a LAYOUT effect, in
  // the same commit that sets `inert` and before the browser's focus fix-up
  // can move focus out of the now-inert subtree.
  const resumeFocusRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (suspended) {
      const active = document.activeElement;
      resumeFocusRef.current =
        active instanceof HTMLElement && drawerRef.current?.contains(active) ? active : null;
      return;
    }
    const target = resumeFocusRef.current;
    resumeFocusRef.current = null;
    if (!target) return;
    // The control may have been replaced while the modal was up (an action
    // changes the state, so Compare becomes Add); the drawer is the fallback.
    (target.isConnected ? target : drawerRef.current)?.focus();
  }, [suspended]);

  // The latest props, for a document listener subscribed once per suspension.
  const latest = useRef({ canPrev, canNext, onPrev, onNext, onClose });
  latest.current = { canPrev, canNext, onPrev, onNext, onClose };

  useEffect(() => {
    if (suspended) return;
    function handleKey(e: globalThis.KeyboardEvent) {
      const { canPrev, canNext, onPrev, onNext, onClose } = latest.current;
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const nodes = Array.from(
          drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
        ).filter((n) => !n.hasAttribute("disabled") && n.getAttribute("tabindex") !== "-1");
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (!first || !last) return;
        const active = document.activeElement;
        // Focus on the drawer itself (after a click on its text) or outside it
        // counts as "not on a stop": the next Tab enters at an end.
        const outside = active === drawerRef.current || !drawerRef.current?.contains(active);
        if (e.shiftKey && (active === first || outside)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || outside)) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isTypingTarget(e.target)) return;
      if (e.target instanceof HTMLElement && e.target.closest('[role="tablist"]')) return;
      if ((e.key === "ArrowLeft" || e.key === "k") && canPrev) {
        e.preventDefault();
        onPrev();
      } else if ((e.key === "ArrowRight" || e.key === "j") && canNext) {
        e.preventDefault();
        onNext();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [suspended]);

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: a pointer-only
          dismiss surface; Escape is the keyboard path (the document listener). */}
      <div
        aria-hidden="true"
        data-testid={tid("-scrim")}
        inert={suspended}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-bg opacity-60"
      />
      <div
        ref={drawerRef}
        // biome-ignore lint/a11y/useSemanticElements: a native <dialog> opened with showModal() would put the page's compare modal and the toasts under the top layer; see the module docstring.
        role="dialog"
        aria-modal="true"
        aria-labelledby={nameId}
        // The name until the person loads (and on a 404 or error, when the
        // heading `aria-labelledby` points at never renders).
        aria-label={`${noun.singular} details`}
        data-testid={tid()}
        // Focusable, so a click on plain content lands focus HERE rather than
        // on <body>; see the module docstring before removing it.
        tabIndex={-1}
        inert={suspended}
        data-suspended={suspended ? "true" : undefined}
        className={`fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-border bg-bg shadow-lg focus:outline-none ${
          size === "half" ? "lg:w-1/2 lg:max-w-4xl" : "lg:w-5/6 lg:max-w-6xl"
        }`}
      >
        <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2">
          <div
            className="min-w-0 flex-1 truncate text-h3 text-text"
            data-testid={tid("-context")}
          >
            From <span className="font-normal text-text-secondary">“{listTitle}”</span>
          </div>
          <div
            className="flex items-center gap-1"
            aria-describedby={position || positionLoading ? undefined : hintId}
          >
            <button
              type="button"
              aria-label={`Previous ${noun.lower}`}
              title="Previous (←)"
              ref={prevRef}
              data-testid={tid("-prev")}
              disabled={!canPrev}
              onClick={onPrev}
              className={iconButton}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <span
              className="min-w-16 text-center text-caption tabular-nums text-text-secondary"
              data-testid={tid("-position")}
            >
              {position
                ? `${position.index + 1} of ${position.total}`
                : positionLoading
                  ? "…"
                  : "—"}
            </span>
            <button
              type="button"
              aria-label={`Next ${noun.lower}`}
              title="Next (→)"
              ref={nextRef}
              data-testid={tid("-next")}
              disabled={!canNext}
              onClick={onNext}
              className={iconButton}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <CopyLinkPopover links={shareLinks} />
          {pagePath && (
            <Link
              to={pagePath}
              data-testid={tid("-open-page")}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-label text-text hover:border-accent hover:text-accent-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
              Open as page
            </Link>
          )}
          <button
            ref={closeRef}
            type="button"
            aria-label="Close panel"
            title="Close (Esc)"
            data-testid={tid("-close")}
            onClick={onClose}
            className={iconButton}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {!position && !positionLoading && (
          <p
            id={hintId}
            className="border-b border-border bg-surface px-4 py-1 text-caption text-text-muted"
            data-testid={positionError ? tid("-rows-error") : tid("-off-page")}
          >
            {positionError
              ? "The list's rows could not be loaded, so ‹ › are off."
              : `This ${noun.lower} is not on the page of the list you are looking at, so ‹ › are off.`}
          </p>
        )}
        <div className="flex-1 overflow-y-auto">{children(nameId)}</div>
      </div>
    </>
  );
}
